import { Link } from 'react-router';
import Page from '../../components/layout/Page';
import ListItem from '../../components/shared/ListItem';
import { Heading2 } from '../../components/headings';

export function About() {
    return (
        <Page>
            <section className='space-y-2'>
                <Heading2 id='origin'>It's just to fill in what is not enough</Heading2>
                <p>
                    Launched in August 2020, this project is a list of EVERY demon rated, with a little tier put beside
                    it. It originated from the discussion that the in-game demon difficulties are not enough. The
                    division of difficulties for demons is always a subject of discussion, but there isn't a definitive
                    list for this. Well, until now!
                </p>
                <p>This project was created with the following two problems in mind:</p>
                <ol className='ms-2'>
                    <li className='mb-1'>
                        1) Although the official difficulty for demons in-game is good, it is too broad to serve as a
                        reference. Difficulty can vary greatly inside the same demon category.
                    </li>
                    <li>
                        2) There is no default demon difficulty, and all freshly rated demons are deemed Hard Demons by
                        default, making the system even more confusing.
                    </li>
                </ol>
            </section>
            <section className='mt-8 space-y-2'>
                <Heading2 id='mechanism'>The mechanism</Heading2>
                <p>
                    With the official system in mind, each demon difficulty is split into 5 tiers, with the exception of
                    Extreme Demons being split into 19 tiers. Tiers are then named from Tier 1 to Tier 39, with Tier 39
                    being the hardest.
                </p>
                <p>
                    Tiers are determined by user ratings. This means the list is community-driven, one thing that is
                    loved by the owner. User ratings are collected and averaged out to obtain the final tier of a level.
                    The system has been running well since launch, and we have collected over{' '}
                    <span className='link'>1,500,000 ratings</span> for over{' '}
                    <span className='link'>9,600 classic demons</span> covering over 90% of the demons in the game.
                </p>
            </section>
            <section className='mt-8 space-y-2'>
                <Heading2 id='guidelines'>Rating guidelines</Heading2>
                <p>Here are some guidelines that you should consider while voting for the tier of demons:</p>
                <ol className='ms-2'>
                    <li className='mb-5'>
                        <p className='mb-1'>
                            1) Your rating should be based on the difficulty of the whole level, which means:
                        </p>
                        <ul>
                            <ListItem>You should not rate based on the difficulty of the hardest section.</ListItem>
                            <ListItem>
                                You should not rate based on only one criterion, e.g. timing, spamming, etc.
                            </ListItem>
                        </ul>
                    </li>
                    <li className='mb-5'>
                        <p className='mb-1'>
                            2) For levels that contain multiple paths or have bonus objectives, rate according to the
                            easiest path that is not hidden.
                        </p>
                        <ul>
                            <ListItem>
                                A hidden path is a part of the level where it requires previous knowledge of the layout
                                in order to enter or proceed, AND good sight-reading cannot substitute the said
                                information.
                            </ListItem>
                        </ul>
                    </li>
                    <li className='mb-5'>
                        <p className='mb-1'>3) You can safely ignore the official difficulty in-game.</p>
                        <ul>
                            <ListItem>They are in many cases inaccurate, and should not be fully relied on.</ListItem>
                        </ul>
                    </li>
                    <li className='mb-5'>
                        <p className='mb-1'>
                            4) You do not need to follow others' opinions or rate based on what others suggest.
                        </p>
                        <ul>
                            <ListItem>
                                The system is designed to tolerate discrepancies, so differences are accepted.
                            </ListItem>
                            <ListItem>
                                Split decisions are a good thing, they show how subjective the difficulty of a level can
                                be.
                            </ListItem>
                            <ListItem>
                                Do not rate a level so that its tier ends up matching what you think it should be.
                                Respect other people's opinions.
                            </ListItem>
                            <ListItem>
                                Ratings that land nowhere near everyone else's can be given less weight, so they count
                                for less towards the level's tier. Disagreeing is fine, being a huge outlier is not the
                                same thing.
                            </ListItem>
                        </ul>
                    </li>
                    <li className='mb-5'>
                        <p className='mb-1'>5) Tier 21+ demon submissions require video proof.</p>
                        <ul>
                            <ListItem>
                                Every level that is rated tier 21 or higher on GDDL needs proof, as does any level that
                                is an Extreme Demon in-game, even when its tier is below 21.
                            </ListItem>
                            <ListItem>Proof is also required if you want to rate a level tier 21 or higher.</ListItem>
                            <ListItem>
                                For levels that are tier 31 or higher, audible clicks are required in the proof.
                            </ListItem>
                            <ListItem>
                                The entire run must be included; from at least the first click to the end-screen. The
                                end-screen must be included.
                            </ListItem>
                        </ul>
                    </li>
                    <li className='mb-5'>
                        <p className='mb-1'>6) Physics bypass is not allowed.</p>
                        <ul>
                            <ListItem>CBF is allowed.</ListItem>
                        </ul>
                    </li>
                    <li className='mb-5'>
                        <p className='mb-1'>7) The level must be beaten on the official version or a verified LDM.</p>
                    </li>
                </ol>
            </section>
            <section className='mt-8 space-y-2'>
                <Heading2 id='howToRate'>How to rate</Heading2>
                <p>
                    To rate a level, it is required that you are logged in. If you do not have an account, head over to{' '}
                    <Link to='/signup' className='link'>
                        sign-up
                    </Link>{' '}
                    to create an account. Afterwards, you must link your account to Discord to start sending
                    submissions. To do that, click on your profile icon in the top right corner, then click on
                    "Settings", then click on "Connections" and then select the Discord icon.
                </p>
                <p>
                    If you are logged in and you are viewing the information on a level, there should be a section right
                    under the level's information with a blue button that either says "Submit" or "Save" if you have
                    already submitted a rating for that level. Switch to the status you want to submit and optionally
                    add tier, enjoyment and percent. Then, click the button to submit your rating. If you want to add
                    additional information like proof or attempt count, click the "advanced" text that's highlighted in
                    yellow next to the submit button.
                </p>
                <img className='w-11/12 mx-auto' src='/images/howToRate.png' alt='' />
                <p>
                    It's also possible to send submissions by opening the navigation menu (click your profile icon) and
                    clicking on the big, blue "Submit" button.
                </p>
            </section>
            <section className='mt-8 space-y-2'>
                <Heading2 id='proof'>Proof</Heading2>
                <p>
                    Proof must come in the form of a video. GDDL does not host videos so you will have to upload the
                    video somewhere else.
                </p>
                <p>Here is a list of accepted URLs:</p>
                <pre>
                    <p>{`https://www.youtube.com/watch?v={id}`}</p>
                    <p>{`https://m.youtube.com/watch?v={id}`}</p>
                    <p>{`https://youtube.com/watch?v={id}`}</p>
                    <p>{`https://youtube.com/watch/{id}`}</p>
                    <p>{`https://youtube.com/live/{id}?t={time}`}</p>
                    <p>{`https://youtu.be/{id}`}</p>
                    <p>{`https://www.twitch.tv/videos/{id}`}</p>
                    <p>{`https://twitch.tv/videos/{id}`}</p>
                    <p>{`https://drive.google.com/file/d/{id}`}</p>
                    <p>{`https://www.medal.tv/clips/{id}`}</p>
                    <p>{`https://www.medal.tv/.../clips/{id}`}</p>
                    <p>{`https://medal.tv/clips/{id}`}</p>
                    <p>{`https://medal.tv/.../clips/{id}`}</p>
                    <p>{`https://www.outplayed.tv/media/{id}`}</p>
                    <p>{`https://www.outplayed.tv/.../{id}`}</p>
                    <p>{`https://outplayed.tv/media/{id}`}</p>
                    <p>{`https://outplayed.tv/.../{id}`}</p>
                    <p>{`https://www.bilibili.com/video/{id}`}</p>
                    <p>{`https://m.bilibili.com/video/{id}`}</p>
                    <p>{`https://bilibili.com/video/{id}`}</p>
                </pre>
                <p>
                    Proof is optional for most levels below tier 21. It is required when the level is an Extreme Demon
                    in-game, or when you want to rate it tier 21 or higher, no matter what its GDDL tier is. You are
                    still allowed to add proof to anything else.
                </p>
                <p>For levels that are tier 31 or higher, audible clicks are required in the proof.</p>
                <p>
                    The entire run must be included; from at least the first click to the end-screen. The end-screen
                    must be included.
                </p>
            </section>
            <section className='mt-8 space-y-2'>
                <Heading2 id='autoAccept'>Auto accept system</Heading2>
                <p>
                    You may have noticed that some of your submissions get instantly accepted and others don't. Whether
                    that happens depends on the tier of the level, how much of an outlier your rating is, and how many
                    ratings the level already has. The point is to keep the queue for submissions that actually need a
                    staff member to look at them, instead of yet another tier 1 The Lightning Road rating.
                </p>
                <p>
                    The exact specifics can vary, but at the time of writing a submission goes to the queue instead of
                    being accepted straight away when any of the following apply:
                </p>
                <ul>
                    <ListItem>
                        The level is extreme. That means tier 21 or higher, a demon difficulty of extreme, or you rating
                        it tier 21 or higher yourself. Everything below that can be accepted automatically.
                    </ListItem>
                    <ListItem>
                        Your rating is an outlier. Ratings far from the level's current rating get held back, but how
                        far is too far depends on the level. Where opinions are split you have a lot more room than on a
                        level with a firm consensus.
                    </ListItem>
                    <ListItem>
                        The level has fewer than 5 ratings, so there is nothing to compare yours against.
                    </ListItem>
                </ul>
                <p>
                    Editing a submission you already have accepted does not send it back to the queue, as long as you
                    leave the tier, proof, progress and whether it was a two player completion alone. Levels you mark as
                    plan to beat are always accepted immediately.
                </p>
                <p>
                    Submissions that are already accepted on{' '}
                    <a href='https://aredl.net/' target='_blank' rel='noopener noreferrer' className='underline-t'>
                        AREDL
                    </a>{' '}
                    will skip the queue entirely, as long as the submitted proof link matches the public proof link on
                    your linked AREDL profile.
                </p>
            </section>
        </Page>
    );
}
